/**
 * HarvestBoard — dedicated flow for fishermen, small farmers, home
 * growers, and hobby fishers to post a ready-or-nearly-ready harvest.
 *
 * Route: /food-pulse/harvest
 *
 * The producer IS the vendor, so unlike SpotADeal there's no vendor
 * picker. Focus is on: producer type, harvest status, GPS auto-fill,
 * pricing, pickup window, and direct-contact channels (WhatsApp /
 * phone / email — at least one required).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Fish, Sprout, Home, ArrowRight, ArrowLeft, Loader2, Camera, X,
  MapPin, Award, Phone, Mail, MessageCircle, Sparkles, Search, Check,
} from "lucide-react";
import axios from "axios";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SeoHead from "@/components/SeoHead";

const API = process.env.REACT_APP_BACKEND_URL;
const GREEN = "#0F3D2E";
const AMBER = "#E4A140";
const CREAM = "#FBF7EF";

const PRODUCER_TYPES = [
  { key: "fisherman",     label: "Fisherman / Fisher",   icon: Fish,   blurb: "Boats, docks, tidal catch" },
  { key: "hobby_fisher",  label: "Hobby fisher",         icon: Fish,   blurb: "Weekend catch to share" },
  { key: "small_farmer",  label: "Small farmer",         icon: Sprout, blurb: "Farmstand, roadside stall" },
  { key: "home_grower",   label: "Home grower",          icon: Home,   blurb: "Backyard, allotment, balcony" },
];

const MAX_UPLOAD = 5 * 1024 * 1024;

export default function HarvestBoard() {
  const { user } = useAuth();
  const [producerType, setProducerType] = useState("");
  const [foodItem, setFoodItem] = useState(null);
  const [foodQ, setFoodQ] = useState("");
  const [foodResults, setFoodResults] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [harvestStatus, setHarvestStatus] = useState("ready_now");
  const [harvestReadyAt, setHarvestReadyAt] = useState("");
  const [coords, setCoords] = useState(null);
  const [gpsErr, setGpsErr] = useState(null);
  const [pickupAddress, setPickupAddress] = useState("");
  const [pickupStart, setPickupStart] = useState("");
  const [pickupEnd, setPickupEnd] = useState("");
  const [origPrice, setOrigPrice] = useState("");
  const [dealPrice, setDealPrice] = useState("");
  const [quantity, setQuantity] = useState("");
  const [unit, setUnit] = useState("lb");
  const [deliveryRadius, setDeliveryRadius] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [expireHours, setExpireHours] = useState(24);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const requestGPS = useCallback(() => {
    if (!navigator.geolocation) { setGpsErr("Geolocation not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      (err) => setGpsErr(err.message),
      { timeout: 8000, maximumAge: 60000 }
    );
  }, []);
  useEffect(() => { requestGPS(); }, [requestGPS]);

  // Food catalog search
  useEffect(() => {
    if (foodQ.length < 2) { setFoodResults([]); return; }
    let cancel = false;
    axios.get(`${API}/api/food-items`, { params: { q: foodQ, limit: 8 } })
      .then((r) => { if (!cancel) setFoodResults(r.data.items || r.data || []); })
      .catch(() => !cancel && setFoodResults([]));
    return () => { cancel = true; };
  }, [foodQ]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_UPLOAD) return alert(`Photo too large (${MAX_UPLOAD / 1024 / 1024}MB max)`);
    setUploading(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const rd = new FileReader(); rd.onload = () => res(rd.result); rd.onerror = rej;
        rd.readAsDataURL(f);
      });
      const r = await http.post(`/entity/upload`, { image: dataUrl });
      setPhotoUrl(r.data.url);
    } catch (err) { alert(err?.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };

  const canSubmit = producerType && title.trim().length >= 3 && coords &&
    (whatsapp.trim() || phone.trim() || email.trim()) &&
    (harvestStatus === "ready_now" || (harvestStatus === "ready_soon" && harvestReadyAt));

  const submit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const r = await http.post(`/deals/producer`, {
        producer_type: producerType,
        food_item_slug: foodItem?.slug,
        title: title.trim(),
        description: description.trim() || undefined,
        scout_photo_url: photoUrl,
        original_price: origPrice ? Number(origPrice) : undefined,
        deal_price: dealPrice ? Number(dealPrice) : undefined,
        quantity_available: quantity ? Number(quantity) : undefined,
        unit,
        harvest_status: harvestStatus,
        harvest_ready_at: harvestStatus === "ready_soon" ? harvestReadyAt : undefined,
        lat: coords.lat, lon: coords.lon,
        pickup_address: pickupAddress.trim() || undefined,
        pickup_window_start: pickupStart || undefined,
        pickup_window_end: pickupEnd || undefined,
        delivery_radius_km: deliveryRadius ? Number(deliveryRadius) : undefined,
        contact_whatsapp: whatsapp.trim() || undefined,
        contact_phone: phone.trim() || undefined,
        contact_email: email.trim() || undefined,
        expires_in_hours: Number(expireHours),
      });
      setResult(r.data);
    } catch (e) { alert(e?.response?.data?.detail || "Submit failed"); }
    finally { setSubmitting(false); }
  };

  if (!user) return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: CREAM, color: GREEN }}>
      <div className="max-w-md text-center">
        <Sprout className="w-14 h-14 mx-auto mb-4" style={{ color: AMBER }} />
        <h1 className="font-display text-3xl mb-3">Harvest Board</h1>
        <p className="opacity-80 mb-6">Sign in to post your fresh catch, harvest, or backyard produce.</p>
        <Link to="/login" className="rounded-full px-6 py-3 font-semibold text-white" style={{ background: GREEN }} data-testid="harvest-login-cta">Sign in</Link>
      </div>
    </div>
  );

  if (result) return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: CREAM, color: GREEN }} data-testid="harvest-success">
      <div className="max-w-md text-center bg-white rounded-3xl p-8 shadow-xl" style={{ border: "1px solid #E9D8B4" }}>
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: AMBER }}>
          <Award className="w-8 h-8" style={{ color: GREEN }} />
        </div>
        <h1 className="font-display text-3xl mb-2">Harvest posted!</h1>
        <div className="text-lg mb-4">+<strong>{result.points_awarded}</strong> points (Waste Warrior)</div>
        <p className="opacity-80 text-sm mb-6">
          People with <em>{result.food_item_name || "this item"}</em> on their wishlist will get an instant ping.
          Anyone browsing nearby will see your harvest in their feed.
        </p>
        <div className="flex gap-2 justify-center">
          <Link to={`/food-pulse/deals/${result.slug}`} className="rounded-full px-4 py-2 text-white text-sm font-semibold" style={{ background: GREEN }} data-testid="harvest-view">View harvest</Link>
          <button onClick={() => { setResult(null); setProducerType(""); setTitle(""); setDescription(""); setPhotoUrl(null); }}
                  className="rounded-full px-4 py-2 text-sm font-semibold border-2" style={{ borderColor: GREEN, color: GREEN }}>
            Post another
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: CREAM, color: GREEN }} data-testid="harvest-board">
      <SeoHead section="food-pulse-harvest" title="Harvest Board — Food Pulse" description="Fishermen, small farmers, and home growers post fresh harvest with GPS + direct contact." />

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: GREEN, color: "#fff" }}>
        <div className="absolute inset-0"
             style={{ backgroundImage: `linear-gradient(115deg, ${GREEN} 0%, rgba(15,61,46,0.85) 60%, rgba(15,61,46,0.4) 100%), url('')`, backgroundSize: "cover", backgroundPosition: "center" }}
             aria-hidden />
        <div className="relative max-w-3xl mx-auto px-4 py-10 sm:py-14">
          <div className="inline-flex items-center gap-2 rounded-full border-2 px-4 py-1.5 text-[11px] font-mono uppercase tracking-[0.28em] mb-4"
               style={{ borderColor: AMBER, color: "#F6C87A" }}>
            <Sprout className="w-3.5 h-3.5" /> Harvest board
          </div>
          <h1 className="font-display text-4xl sm:text-5xl leading-tight">
            Fresh from your <span style={{ color: AMBER }}>hands.</span>
          </h1>
          <p className="mt-4 opacity-90 max-w-xl text-sm sm:text-base">
            Fishermen, small farmers, home growers — post what you have (or will have soon).
            We ping every user in your area who wants it. Direct contact, no middlemen.
          </p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 py-10 grid gap-6">
        {/* 1. Producer type */}
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: AMBER }}>1 · Who are you?</div>
          <div className="grid grid-cols-2 gap-3">
            {PRODUCER_TYPES.map(({ key, label, icon: Ico, blurb }) => (
              <button key={key} onClick={() => setProducerType(key)}
                      className={`text-left rounded-2xl border-2 p-4 transition-all hover:-translate-y-0.5 ${producerType === key ? "shadow-lg" : ""}`}
                      style={{ borderColor: producerType === key ? GREEN : "#E9D8B4",
                               background: producerType === key ? "#F0F5F1" : "#fff" }}
                      data-testid={`harvest-type-${key}`}>
                <Ico className="w-6 h-6 mb-2" style={{ color: producerType === key ? GREEN : AMBER }} />
                <div className="font-display text-lg leading-tight">{label}</div>
                <div className="text-[11px] opacity-70">{blurb}</div>
              </button>
            ))}
          </div>
        </div>

        {/* 2. What's ready */}
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: AMBER }}>2 · What's ready?</div>
          <div className="rounded-2xl border p-4" style={{ borderColor: "#E9D8B4", background: "#fff" }}>
            {/* Optional catalog search */}
            <div className="flex items-center gap-2 rounded-full border px-3 py-2 mb-3" style={{ borderColor: "#E9D8B4", background: CREAM }}>
              <Search className="w-4 h-4 opacity-50" />
              <input value={foodQ} onChange={(e) => setFoodQ(e.target.value)} placeholder="Search catalog: salmon, tomato, mango…"
                     className="flex-1 outline-none text-sm bg-transparent" data-testid="harvest-food-search" />
              {foodItem && <button onClick={() => { setFoodItem(null); setFoodQ(""); }} className="opacity-60"><X className="w-4 h-4" /></button>}
            </div>
            {foodResults.length > 0 && !foodItem && (
              <div className="grid gap-1 mb-3 max-h-48 overflow-y-auto">
                {foodResults.map((it) => (
                  <button key={it.slug} onClick={() => { setFoodItem(it); setFoodResults([]); setFoodQ(it.name); if (!title) setTitle(`${it.name} — fresh harvest`); }}
                          className="flex items-center gap-2 rounded-lg border p-2 hover:border-emerald-800 transition-all text-left"
                          style={{ borderColor: "#E9D8B4", background: "#fff" }}
                          data-testid={`harvest-food-${it.slug}`}>
                    <div className="w-8 h-8 rounded overflow-hidden" style={{ background: "#F6C87A" }}>
                      {(it.hero_image || it.image_url) && <img src={it.hero_image || it.image_url} className="w-full h-full object-cover" alt="" />}
                    </div>
                    <div className="text-sm font-semibold flex-1">{it.name}</div>
                  </button>
                ))}
              </div>
            )}
            {foodItem && (
              <div className="text-xs opacity-70 mb-3 flex items-center gap-2">
                <Check className="w-3.5 h-3.5" style={{ color: GREEN }} /> {foodItem.name} linked from catalog
              </div>
            )}

            <input value={title} onChange={(e) => setTitle(e.target.value)}
                   placeholder="Title (e.g. Wild-caught mackerel — just landed) *"
                   className="w-full rounded-xl border px-3 py-2 mb-2 text-sm" style={{ borderColor: "#E9D8B4" }}
                   data-testid="harvest-title" />
            <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)}
                      placeholder="Notes (freshness, size, boat name, farm name…)"
                      className="w-full rounded-xl border px-3 py-2 mb-3 text-sm" style={{ borderColor: "#E9D8B4" }} />

            {/* Harvest state */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <label className="text-sm">
                <span className="text-[11px] font-mono uppercase tracking-widest opacity-70">Ready</span>
                <select value={harvestStatus} onChange={(e) => setHarvestStatus(e.target.value)}
                        className="mt-1 w-full rounded-xl border px-3 py-2 bg-white" style={{ borderColor: "#E9D8B4" }}
                        data-testid="harvest-status">
                  <option value="ready_now">✅ Ready now</option>
                  <option value="ready_soon">🌱 Ready soon</option>
                </select>
              </label>
              {harvestStatus === "ready_soon" && (
                <label className="text-sm">
                  <span className="text-[11px] font-mono uppercase tracking-widest opacity-70">Ready date</span>
                  <input type="date" value={harvestReadyAt} onChange={(e) => setHarvestReadyAt(e.target.value)}
                         className="mt-1 w-full rounded-xl border px-3 py-2" style={{ borderColor: "#E9D8B4" }}
                         data-testid="harvest-ready-at" />
                </label>
              )}
            </div>

            {/* Photo */}
            <div className="rounded-xl border-2 border-dashed p-3 flex items-center gap-3" style={{ borderColor: AMBER, background: "#FFF7E4" }}>
              {photoUrl ? (
                <div className="relative">
                  <img src={photoUrl} alt="" className="w-16 h-16 object-cover rounded-lg" />
                  <button onClick={() => setPhotoUrl(null)} className="absolute -top-1 -right-1 bg-white rounded-full p-0.5 border shadow"><X className="w-3 h-3" /></button>
                </div>
              ) : (
                <button onClick={() => fileRef.current?.click()} disabled={uploading}
                        className="w-16 h-16 rounded-lg border-2 border-dashed flex items-center justify-center flex-shrink-0"
                        style={{ borderColor: AMBER, color: AMBER }}
                        data-testid="harvest-photo-btn">
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
                </button>
              )}
              <div className="text-xs opacity-70">A real photo of the catch or harvest builds trust fast.</div>
              <input type="file" accept="image/*" capture="environment" ref={fileRef} onChange={onFile} className="hidden" />
            </div>
          </div>
        </div>

        {/* 3. Pricing */}
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: AMBER }}>3 · Price &amp; quantity</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <label className="text-sm">
              <span className="text-[11px] opacity-70">Price</span>
              <input type="number" step="0.01" value={dealPrice} onChange={(e) => setDealPrice(e.target.value)}
                     className="mt-1 w-full rounded-xl border px-3 py-2" style={{ borderColor: "#E9D8B4" }}
                     data-testid="harvest-price" />
            </label>
            <label className="text-sm">
              <span className="text-[11px] opacity-70">Original $ (optional)</span>
              <input type="number" step="0.01" value={origPrice} onChange={(e) => setOrigPrice(e.target.value)}
                     className="mt-1 w-full rounded-xl border px-3 py-2" style={{ borderColor: "#E9D8B4" }} />
            </label>
            <label className="text-sm">
              <span className="text-[11px] opacity-70">Qty</span>
              <input type="number" min={0} value={quantity} onChange={(e) => setQuantity(e.target.value)}
                     className="mt-1 w-full rounded-xl border px-3 py-2" style={{ borderColor: "#E9D8B4" }}
                     data-testid="harvest-qty" />
            </label>
            <label className="text-sm">
              <span className="text-[11px] opacity-70">Unit</span>
              <input value={unit} onChange={(e) => setUnit(e.target.value)}
                     className="mt-1 w-full rounded-xl border px-3 py-2" style={{ borderColor: "#E9D8B4" }} />
            </label>
          </div>
        </div>

        {/* 4. Location + Pickup */}
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: AMBER }}>4 · Where &amp; when</div>
          <div className="rounded-2xl border p-4" style={{ borderColor: "#E9D8B4", background: "#fff" }}>
            <div className="flex items-center gap-2 mb-3 text-sm">
              <MapPin className="w-4 h-4" style={{ color: coords ? GREEN : AMBER }} />
              {coords ? (
                <span>GPS locked: <code className="text-xs">{coords.lat.toFixed(4)}, {coords.lon.toFixed(4)}</code></span>
              ) : (
                <span className="opacity-70">
                  {gpsErr || "Locating…"} {" "}
                  <button onClick={requestGPS} className="underline">Try again</button>
                </span>
              )}
            </div>
            <input value={pickupAddress} onChange={(e) => setPickupAddress(e.target.value)}
                   placeholder="Pickup address (dock, farm gate, house #)"
                   className="w-full rounded-xl border px-3 py-2 mb-3 text-sm" style={{ borderColor: "#E9D8B4" }}
                   data-testid="harvest-pickup-address" />
            <div className="grid grid-cols-2 gap-3 mb-3">
              <label className="text-sm">
                <span className="text-[11px] opacity-70">Pickup from</span>
                <input type="datetime-local" value={pickupStart} onChange={(e) => setPickupStart(e.target.value)}
                       className="mt-1 w-full rounded-xl border px-3 py-2" style={{ borderColor: "#E9D8B4" }} />
              </label>
              <label className="text-sm">
                <span className="text-[11px] opacity-70">Pickup until</span>
                <input type="datetime-local" value={pickupEnd} onChange={(e) => setPickupEnd(e.target.value)}
                       className="mt-1 w-full rounded-xl border px-3 py-2" style={{ borderColor: "#E9D8B4" }} />
              </label>
            </div>
            <label className="text-sm">
              <span className="text-[11px] opacity-70">Will deliver within (km, optional)</span>
              <input type="number" min={0} max={200} value={deliveryRadius} onChange={(e) => setDeliveryRadius(e.target.value)}
                     className="mt-1 w-full rounded-xl border px-3 py-2" style={{ borderColor: "#E9D8B4" }} />
            </label>
          </div>
        </div>

        {/* 5. Contact */}
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: AMBER }}>5 · How can they reach you? (any one)</div>
          <div className="grid gap-2">
            <label className="text-sm flex items-center gap-2 rounded-xl border px-3 py-2" style={{ borderColor: "#E9D8B4", background: "#fff" }}>
              <MessageCircle className="w-4 h-4" style={{ color: "#25D366" }} />
              <input value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)}
                     placeholder="WhatsApp (with country code, e.g. +15035550100)"
                     className="flex-1 outline-none text-sm bg-transparent"
                     data-testid="harvest-whatsapp" />
            </label>
            <label className="text-sm flex items-center gap-2 rounded-xl border px-3 py-2" style={{ borderColor: "#E9D8B4", background: "#fff" }}>
              <Phone className="w-4 h-4" style={{ color: GREEN }} />
              <input value={phone} onChange={(e) => setPhone(e.target.value)}
                     placeholder="Phone number"
                     className="flex-1 outline-none text-sm bg-transparent"
                     data-testid="harvest-phone" />
            </label>
            <label className="text-sm flex items-center gap-2 rounded-xl border px-3 py-2" style={{ borderColor: "#E9D8B4", background: "#fff" }}>
              <Mail className="w-4 h-4" style={{ color: AMBER }} />
              <input value={email} onChange={(e) => setEmail(e.target.value)}
                     placeholder="Email"
                     className="flex-1 outline-none text-sm bg-transparent"
                     data-testid="harvest-email" />
            </label>
          </div>
        </div>

        {/* 6. Expiry */}
        <label className="text-sm">
          <span className="text-[10px] font-mono uppercase tracking-widest opacity-70">Post expires in (hours) — max 72</span>
          <input type="number" min={1} max={72} value={expireHours} onChange={(e) => setExpireHours(e.target.value)}
                 className="mt-1 w-full rounded-xl border px-3 py-2" style={{ borderColor: "#E9D8B4" }}
                 data-testid="harvest-expire" />
        </label>

        <div className="rounded-2xl p-4 flex items-center gap-3" style={{ background: "#F0F5F1" }}>
          <Award className="w-5 h-5 flex-shrink-0" style={{ color: GREEN }} />
          <div className="text-sm">
            You'll earn <strong>+30 points</strong> (Waste Warrior bonus).
            Anyone in your area with this on their wishlist gets an instant alert — in-app, email, and push.
          </div>
        </div>

        <div className="flex items-center justify-between pt-2">
          <Link to="/food-pulse/deals" className="text-sm opacity-70 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Cancel</Link>
          <button onClick={submit} disabled={!canSubmit || submitting}
                  className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold text-white disabled:opacity-40"
                  style={{ background: AMBER, color: GREEN }}
                  data-testid="harvest-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Publish harvest
          </button>
        </div>
      </div>
    </div>
  );
}
